/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { css, jsx } from '@compiled/react';

import { SmartCardProvider } from '@atlaskit/link-provider';

import { LinkListCompact } from './link-list-compact';
import { LinkListFull } from './link-list-full';
import type { LinkListSize, OnLinkClick } from './types';

interface LinkListProps {
	/** Max items shown after the user clicks "Show more". Defaults to all items when omitted. */
	expandedMaxSources?: number;
	headerVisible?: boolean;
	/** Max items shown in the initial, collapsed state. */
	maxSources?: number;
	onLinkClick?: OnLinkClick;
	/** Called when the "View all" link is clicked. Navigation still happens via `viewAllUrl`. */
	onViewAllLinkClick?: () => void;
	/**
	 * Whether to show the show more/less expand button when the number of URLs
	 * exceeds `maxSources`. Defaults to `true`. Set to `false` to show all
	 * items up to `maxSources` with no expand/collapse toggle.
	 */
	showExpand?: boolean;
	size?: LinkListSize;
	/**
	 * Identifies the surface/product the link list is rendered in (e.g.
	 * `'luminaSAIN'`). Added to the analytics context and forwarded onto events
	 * fired from within the link list (e.g. the errored event).
	 */
	source?: string;
	urls: string[];
	viewAllUrl?: string;
}

const containerRoot = css({
	width: '100%',
	containerType: 'inline-size',
	containerName: 'link-list-knowledge-card-container',
});

const showCompact = css({
	display: 'block',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container link-list-knowledge-card-container (min-width: 481px)': {
		display: 'none',
	},
});

const showFull = css({
	display: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container link-list-knowledge-card-container (min-width: 481px)': {
		display: 'block',
	},
});

export const DEFAULT_MAX_SOURCES = 4;

export const LinkList = ({
	urls,
	onLinkClick,
	size,
	maxSources,
	expandedMaxSources,
	showExpand,
	viewAllUrl,
	onViewAllLinkClick,
	source,
}: LinkListProps): JSX.Element => {
	return (
		<div css={containerRoot}>
			<SmartCardProvider>
				<div css={showCompact}>
					<LinkListCompact
						urls={urls}
						onLinkClick={onLinkClick}
						size={size}
						maxSources={maxSources}
						expandedMaxSources={expandedMaxSources}
						showExpand={showExpand}
						viewAllUrl={viewAllUrl}
						onViewAllLinkClick={onViewAllLinkClick}
						source={source}
					/>
				</div>
				<div css={showFull}>
					<LinkListFull
						urls={urls}
						onLinkClick={onLinkClick}
						maxSources={maxSources}
						expandedMaxSources={expandedMaxSources}
						showExpand={showExpand}
						viewAllUrl={viewAllUrl}
						onViewAllLinkClick={onViewAllLinkClick}
						source={source}
					/>
				</div>
			</SmartCardProvider>
		</div>
	);
};
